// externals
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { offerSessionUtils } from '@makemydeal/dr-shared-ui-utils';
import React from 'react';

// interstate components
import { Alert } from '@interstate/components/Alert';
import { Button } from '@interstate/components/Button';

// consts
import { HOST_APP, REMOTE_MODULE_ERROR_EVENT } from '../../constants';

// types
import type { RemoteModuleErrorBoundaryBaseProps, RemoteModuleErrorBoundaryState } from '../../types';

/**
 * React error boundary used to wrap the lazy-loaded Common Consumer Card remote modules
 * rendered by Sales Check-in (SimplifiedCardModule, FindOrCreateModule, CustomerEditModule).
 *
 * Without this boundary, render-time exceptions thrown from inside the federated remote bubble
 * up uncaught and leave the user with a blank panel. This boundary surfaces a friendly fallback
 * UI — an Interstate <Alert> with a Retry button, unless the host passes its own `fallback` — and
 * emits a `SCI:CCC:ModuleRenderError` page action to New Relic so the team can detect and triage
 * CCC PBC failures.
 *
 * This is the presentational half of the boundary: it takes `dealerId` as a prop because a class
 * component cannot read Redux via hooks. Hosts should render the store-connected
 * `RemoteModuleErrorBoundary` wrapper instead; use this class directly only in tests or where
 * `dealerId` is already at hand.
 */
export class RemoteModuleErrorBoundaryBase extends React.Component<
    RemoteModuleErrorBoundaryBaseProps,
    RemoteModuleErrorBoundaryState
> {
    constructor(props: RemoteModuleErrorBoundaryBaseProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): RemoteModuleErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        const { moduleName, commonConsumerId, dealerId, onError } = this.props;
        onError?.();
        logNewRelicPageAction(REMOTE_MODULE_ERROR_EVENT, {
            environment: offerSessionUtils.getEnvironmentType(),
            hostApp: HOST_APP,
            moduleName,
            commonConsumerId: commonConsumerId ?? '',
            dealerId,
            errorMessage: error.message,
            componentStack: errorInfo.componentStack
        });
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null });
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback !== undefined) {
                return this.props.fallback;
            }
            return (
                <Alert
                    data-testid={`remote-module-error-${this.props.moduleName}`}
                    title="Customer panel failed to load"
                    type="error"
                    role="alert"
                >
                    Please try again. If the problem persists, contact support.
                    <div style={{ marginTop: '0.5rem' }}>
                        <Button
                            data-testid={`remote-module-error-retry-${this.props.moduleName}`}
                            buttonStyle="secondary"
                            size="small"
                            onClick={this.handleRetry}
                        >
                            Retry
                        </Button>
                    </div>
                </Alert>
            );
        }
        return this.props.children;
    }
}
